import React, { useEffect, useState, useRef } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  MenuItem,
  CardContent,
  Card,
} from "@mui/material";

import PaymentsIcon from "@mui/icons-material/Payments";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";

import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  Timestamp,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
  doc,
  deleteDoc,
  getAggregateFromServer,
  sum,
} from "firebase/firestore";

export default function DealerAccount() {
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
  });

  const printRef = useRef();

  const [user, setUser] = useState(null);
  const [propertyId, setPropertyId] = useState(null);

  const [dealers, setDealers] = useState([]);
  const [history, setHistory] = useState([]);

  const [payOpen, setPayOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);

  const [selectedDealer, setSelectedDealer] = useState(null);

  const [currentPaid, setCurrentPaid] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [payType, setPayType] = useState("Cash");
  const [paidDate, setPaidDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  /* ================= AUTH + PROPERTY ================= */
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) return;
      setUser(u);

      const roleSnap = await getDoc(doc(db, "authroles", u.uid));
      if (roleSnap.exists()) {
        setPropertyId(roleSnap.data().propertyId);
      }
    });

    return () => unsub();
  }, []);

  /* ================= FETCH DEALERS ================= */
  useEffect(() => {
    if (!propertyId) return;

    const q = query(
      collection(db, "dealers"),
      where("propertyId", "==", propertyId),
      orderBy("created", "desc")
    );

    const unsub = onSnapshot(q, async (snap) => {
      const list = await Promise.all(
        snap.docs.map(async (d) => {
          const paidQ = query(
            collection(db, "dealeracchistory"),
            where("dealerId", "==", d.id),
            where("propertyId", "==", propertyId)
          );

          const purchaseQ = query(
            collection(db, "purchases"),
            where("dealerId", "==", d.id),
            where("propertyId", "==", propertyId)
          );

          const paidAgg = await getAggregateFromServer(paidQ, {
            paid: sum("paidamount"),
          });

          const totalAgg = await getAggregateFromServer(purchaseQ, {
            total: sum("grandtotal"),
          });

          const total = totalAgg.data().total || 0;
          const paid = paidAgg.data().paid || 0;

          return {
            id: d.id,
            ...d.data(),
            total,
            paid,
            remaining: total - paid,
          };
        })
      );

      setDealers(list);
    });

    return () => unsub();
  }, [propertyId]);

  /* ================= PAY DIALOG ================= */
  const openPay = (dealer) => {
    setSelectedDealer(dealer);
    setRemaining(dealer.remaining);
    setCurrentPaid("");
    setPayOpen(true);
  };

  const savePayment = async () => {
    if (Number(currentPaid) > remaining) {
      Toast.fire({ icon: "error", title: "Amount exceeds remaining" });
      return;
    }

    await addDoc(collection(db, "dealeracchistory"), {
      dealerId: selectedDealer.id,
      propertyId,
      paidamount: Number(currentPaid),
      remaining: remaining - Number(currentPaid),
      paytype: payType,
      paiddate: paidDate,
      userid: user.uid,
      created: Timestamp.now(),
      note: "Dealer payment",
    });

    Toast.fire({ icon: "success", title: "Payment added" });
    setPayOpen(false);
  };

  /* ================= VIEW HISTORY ================= */
  const openHistory = (dealer) => {
    setSelectedDealer(dealer);

    const q = query(
      collection(db, "dealeracchistory"),
      where("dealerId", "==", dealer.id),
      where("propertyId", "==", propertyId),
      orderBy("paiddate", "desc")
    );

    onSnapshot(q, (snap) => {
      setHistory(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    });

    setViewOpen(true);
  };

  const handleDelete = async (id) => {
    await deleteDoc(doc(db, "dealeracchistory", id));
    Toast.fire({ icon: "success", title: "Entry deleted" });
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  /* ================= UI ================= */
  return (
    <Box p={3}>

      <Typography variant="h5" fontWeight="bold" color="white" mb={2}>
        Dealer Account
      </Typography>

      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dealer</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {dealers.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.name}</TableCell>
                  <TableCell>{d.mobile}</TableCell>
                  <TableCell>{d.total}</TableCell>
                  <TableCell>{d.paid}</TableCell>
                  <TableCell>{d.remaining}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => openPay(d)}>
                      <PaymentsIcon color="primary" />
                    </IconButton>
                    <IconButton onClick={() => openHistory(d)}>
                      <VisibilityIcon color="success" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* PAY DIALOG */}
          <Dialog open={payOpen} onClose={() => setPayOpen(false)} fullWidth>
            <DialogTitle>Pay Dealer</DialogTitle>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField label="Remaining" value={remaining} fullWidth disabled />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Current Paid"
                    value={currentPaid}
                    onChange={(e) => setCurrentPaid(e.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Pay Type"
                    value={payType}
                    onChange={(e) => setPayType(e.target.value)}
                    fullWidth
                  >
                    {["Cash", "Bank", "RTGS", "Online"].map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    type="date"
                    label="Paid Date"
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={savePayment} variant="contained">
                Save
              </Button>
              <Button color="error" onClick={() => setPayOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* HISTORY DIALOG */}
          <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="md">
            <DialogTitle>Payment History</DialogTitle>
            <DialogContent ref={printRef}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Paid</TableCell>
                    <TableCell>Remaining</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{moment(h.paiddate).format("DD-MM-YYYY")}</TableCell>
                      <TableCell>{h.paidamount}</TableCell>
                      <TableCell>{h.remaining}</TableCell>
                      <TableCell>{h.paytype}</TableCell>
                      <TableCell>
                        <IconButton onClick={() => handleDelete(h.id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={handlePrint}>Print</Button>
              <Button color="error" onClick={() => setViewOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

        </CardContent>
      </Card>
    </Box>
  );
}
